import type {
  ApplicationCredentials,
  ApplicationView,
  ChannelView,
  ContractView,
  CorrelationView,
  DeadLetterView,
  MessageDetail,
  MessageView,
  Overview,
  PermissionAction,
  PermissionView,
} from '@broker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from './client.js';

/**
 * Every read the console does, in one place. Each hook names the endpoint it stands for, so the
 * page components stay about layout and never about transport.
 */

const list = <T>(key: unknown[], path: string, options?: Partial<UseQueryOptions<T>>) =>
  useQuery<T>({ queryKey: key, queryFn: () => api<T>(path), ...options });

export interface ConsoleUser {
  sub: string;
  name: string | null;
  avatarUrl: string | null;
}

/** What this person chose in the console. Kept on the API, per user, not in this browser. */
export interface ConsolePreferences {
  /** Whether the menu entries show the line that explains what each area is for. */
  menuHints: boolean;
}

export interface Me {
  user: ConsoleUser;
  preferences: ConsolePreferences;
}

export const useMe = () => useQuery<Me>({ queryKey: ['me'], queryFn: () => api('/auth/me'), retry: false });

export const useOverview = (since?: string) =>
  list<Overview>(['overview', since ?? '24h'], `/overview${since ? `?since=${encodeURIComponent(since)}` : ''}`, {
    // The live stream keeps the page current; this is only the starting picture.
    staleTime: 30_000,
  });

export const useApplications = () => list<{ applications: ApplicationView[] }>(['applications'], '/applications');

export const useChannels = () => list<{ channels: ChannelView[] }>(['channels'], '/channels');

export const useChannel = (name: string) => list<{ channel: ChannelView }>(['channel', name], `/channels/${encodeURIComponent(name)}`);

export const usePermissions = () => list<{ permissions: PermissionView[] }>(['permissions'], '/permissions');

export const useContracts = () => list<{ contracts: ContractView[] }>(['contracts'], '/contracts');

export const useSubscriptions = () =>
  list<{ subscriptions: Array<{ application: string; channel: string; mode: string; endpoint: string | null; createdAt: string }> }>(
    ['subscriptions'],
    '/subscriptions',
  );

export const useProviders = () =>
  list<{
    providers: Array<{
      name: string;
      title: string;
      available: boolean;
      channels: number;
      notes: string;
      features: { ordering: boolean; competingConsumers: boolean; nativeDeadLetter: boolean; delayedDelivery: boolean };
    }>;
  }>(['providers'], '/providers');

export const useMessages = (query: string) =>
  list<{ messages: MessageView[]; total: number }>(['messages', query], `/messages${query ? `?${query}` : ''}`);

export const useMessage = (id: string) => list<MessageDetail>(['message', id], `/messages/${id}`);

export const useDeadLetters = (openOnly: boolean) =>
  list<{ deadLetters: DeadLetterView[]; total: number }>(['dead-letters', openOnly], `/dead-letters?open=${openOnly}`);

export const useCorrelations = () =>
  list<{ correlations: Array<{ correlationId: string; startedAt: string; endedAt: string | null; messageCount: number }> }>(
    ['correlations'],
    '/correlations',
  );

export const useCorrelation = (id: string) => list<CorrelationView>(['correlation', id], `/correlations/${encodeURIComponent(id)}`);

export interface AuditEntry {
  sequence: number;
  type: string;
  streamKind: string;
  streamId: string;
  version: number;
  occurredAt: string;
  actor: { kind: string; id: string; label: string | null };
  correlationId: string | null;
  body: unknown;
}

export const useAudit = (includeTraffic: boolean) =>
  list<{ events: AuditEntry[]; total: number }>(['audit', includeTraffic], `/audit?includeTraffic=${includeTraffic}&limit=100`);

// Writes -------------------------------------------------------------------------

export function useCreateApplication() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; name: string }) =>
      api<{ application: ApplicationView; credentials: ApplicationCredentials }>('/applications', { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['applications'] }),
  });
}

export function useUpdateApplication() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, ...input }: { slug: string; name?: string; enabled?: boolean }) =>
      api<{ application: ApplicationView }>(`/applications/${slug}`, { method: 'PUT', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['applications'] }),
  });
}

export function useRotateSecret() {
  return useMutation({
    mutationFn: (slug: string) => api<{ credentials: ApplicationCredentials }>(`/applications/${slug}/secret`, { method: 'POST' }),
  });
}

export function useSetPermissions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, channel, actions }: { slug: string; channel: string; actions: PermissionAction[] }) =>
      api<{ permissions: PermissionView[] }>(`/applications/${slug}/channels/${encodeURIComponent(channel)}/permissions`, {
        method: 'PUT',
        body: { actions },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['permissions'] });
      void client.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useCreateChannel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; kind: string; description?: string; maxDeliveryAttempts?: number; retentionDays?: number }) =>
      api<{ channel: ChannelView }>('/channels', { method: 'POST', body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function useReplayDeadLetter() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ replayMessageId: string }>(`/dead-letters/${id}/replay`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dead-letters'] });
      void client.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

export function useRebuildProjections() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ applied: number; sequence: number }>('/admin/rebuild', { method: 'POST' }),
    onSuccess: () => client.invalidateQueries(),
  });
}

/**
 * Shows or hides the menu hints for this user. The cache is moved first: the eye has to answer
 * the click at once, and the round trip only confirms what the screen already says.
 */
export function useSetMenuHints() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (menuHints: boolean) => api<{ preferences: ConsolePreferences }>('/auth/preferences', { method: 'PUT', body: { menuHints } }),
    onMutate: (menuHints) => {
      const previous = client.getQueryData<Me>(['me']);
      if (previous) client.setQueryData<Me>(['me'], { ...previous, preferences: { ...previous.preferences, menuHints } });
      return { previous };
    },
    onError: (_error, _menuHints, context) => {
      if (context?.previous) client.setQueryData<Me>(['me'], context.previous);
    },
    onSuccess: ({ preferences }) => {
      const previous = client.getQueryData<Me>(['me']);
      if (previous) client.setQueryData<Me>(['me'], { ...previous, preferences });
    },
  });
}
